import {
  Field,
  IconButton,
  Modal,
  SingleSelectDropdown,
  Text,
  TextInput,
} from "@carbon6/c6-frontend-design";
import { useEffect, useState, type ChangeEvent, type ReactNode } from "react";
import {
  createEmbodiment,
  createEndEffector,
  listAllEmbodiments,
  listAllEndEffectors,
  updateEmbodiment,
  type Embodiment,
  type EndEffector,
  type Tip,
} from "@/api";
import type { UserRole } from "@/app";
import { hatcheryDropdownModes, hatcheryTextModes } from "@/app/theme";
import { HatModalHeader } from "@/components/modal";
import { formatEndEffector } from "./embodimentDisplay";
import { EmbodimentEditController } from "./EmbodimentEditController";
import { resolveEmbodimentErrorMessage } from "./embodimentErrors";
import styles from "./EmbodimentComposerModal.module.css";

// 컴포저 시작 소스다.
//   - blank: 빈 폼에서 새로 생성한다.
//   - clone: 기존 정보를 바탕으로 새로 생성한다.
//   - edit: 기존 embodiment 를 불러와 수정한다(PATCH).
// 소스 선택 창은 EmbodimentComposerFlow 가 담당한다.
export type EmbodimentComposerSource =
  | { kind: "blank" }
  | { kind: "clone"; embodimentUuid: string }
  | { kind: "edit"; embodimentUuid: string };

export type EmbodimentComposerModalProps = {
  open: boolean;
  role: UserRole | null;
  // 컴포저 시작 소스(빈 폼 / 기존 복제 / 기존 편집).
  source: EmbodimentComposerSource;
  // 부모가 가진 현재 tip 이다. 컴포저는 tip 을 편집하지 않고 그대로 onApply 로 돌려준다.
  // Tip 은 embodiment 속성이 아니라 데이터셋 값이므로 여기서 고르지 않는다.
  currentTip: Tip | null;
  onClose: () => void;
  onApply: (embodiment: Embodiment, tip: Tip | null) => void;
};

type CatalogCreateDialogState = {
  draft: string;
  errorMessage: string | null;
  submitting: boolean;
} | null;

// Embodiment 구성 컴포저다. 식별 스탯과 핸드(End effector)를 한 화면에서 편집한다. 선택은 모두 일반
// 드롭다운(+ '신규' 버튼 → 공용 EE 생성 모달)으로 한다. 저장하면 신규 구성은 POST(create)로,
// 저장된 구성은 PATCH 로 보낸다. Tip 은 embodiment 속성이 아니라 데이터셋 값이므로 여기서 고르지
// 않는다. 부모가 전달한 currentTip 을 그대로 onApply 로 돌려준다.
export function EmbodimentComposerModal({
  open,
  role,
  source,
  currentTip,
  onClose,
  onApply,
}: EmbodimentComposerModalProps) {
  const [embodiments, setEmbodiments] = useState<readonly Embodiment[]>([]);
  const [endEffectors, setEndEffectors] = useState<readonly EndEffector[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [controller, setController] = useState(() => EmbodimentEditController.blank(currentTip));
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const [catalogCreateDialog, setCatalogCreateDialog] = useState<CatalogCreateDialogState>(null);
  const draft = controller.draft;
  const dirty = controller.dirty;
  // 소스가 참조하는 embodiment uuid 다(blank 은 없음). 데이터를 로드한 뒤 초안의 시작점을 정할 때 쓴다.
  const sourceUuid = source.kind === "blank" ? null : source.embodimentUuid;

  function patchDraft(patch: Parameters<EmbodimentEditController["patch"]>[0]): void {
    setController((current) => current.patch(patch));
  }

  useEffect(() => {
    if (!open) {
      setCloseConfirmOpen(false);
      setCatalogCreateDialog(null);
      return;
    }
    setError(null);
    let alive = true;
    setLoading(true);
    Promise.all([listAllEmbodiments(), listAllEndEffectors()])
      .then(([embodimentList, eeList]) => {
        if (!alive) {
          return;
        }
        setEmbodiments(embodimentList);
        setEndEffectors(eeList);
        const found =
          sourceUuid !== null
            ? embodimentList.find((entry) => entry.uuid === sourceUuid)
            : undefined;
        if (source.kind === "edit" && found) {
          setController(EmbodimentEditController.fromEmbodiment(found, currentTip));
        } else if (source.kind === "clone" && found) {
          setController(EmbodimentEditController.cloneFrom(found, currentTip));
        } else {
          setController(EmbodimentEditController.blank(currentTip));
        }
      })
      .catch((caught) => alive && setError(resolveEmbodimentErrorMessage(caught)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // source(kind/uuid)나 currentTip 이 바뀌면 다시 로드한다.
  }, [open, source.kind, sourceUuid, currentTip]);

  const selectedEndEffector = endEffectors.find((ee) => ee.uuid === draft.endEffectorUuid) ?? null;

  function requestClose(): void {
    if (busy) {
      return;
    }
    if (dirty) {
      setCloseConfirmOpen(true);
      return;
    }
    onClose();
  }

  function openCatalogCreateDialog(): void {
    setCatalogCreateDialog({ draft: "", errorMessage: null, submitting: false });
  }

  function updateCatalogCreateDraft(value: string): void {
    setCatalogCreateDialog((current) => (current === null ? null : { ...current, draft: value }));
  }

  async function submitCatalogCreate(): Promise<void> {
    if (catalogCreateDialog === null || catalogCreateDialog.submitting) {
      return;
    }
    const nameValue = catalogCreateDialog.draft.trim();
    if (nameValue === "") {
      setCatalogCreateDialog({ ...catalogCreateDialog, errorMessage: "이름을 입력해 주세요." });
      return;
    }
    setCatalogCreateDialog({ ...catalogCreateDialog, submitting: true, errorMessage: null });
    try {
      const created = await createEndEffector({ name: nameValue }, { role });
      setEndEffectors((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)));
      patchDraft({ endEffectorUuid: created.uuid });
      setCatalogCreateDialog(null);
    } catch (caught) {
      setCatalogCreateDialog({
        ...catalogCreateDialog,
        submitting: false,
        errorMessage: resolveEmbodimentErrorMessage(caught),
      });
    }
  }

  function closeCatalogCreateDialog(): void {
    setCatalogCreateDialog((current) => (current?.submitting ? current : null));
  }

  function discardAndClose(): void {
    setCloseConfirmOpen(false);
    onClose();
  }

  // ── 저장 ───────────────────────────────────────────────────────────────
  function validate(): string | null {
    if (draft.endEffectorUuid === null) return "End effector(핸드)를 선택해 주세요.";
    if (draft.name.trim() === "") return "이름을 입력해 주세요.";
    return null;
  }

  async function handleApply(): Promise<void> {
    const validationError = validate();
    if (validationError !== null) {
      setError(validationError);
      return;
    }
    // 로드한 embodiment 를 편집 없이 그대로 쓰면 쓰기 요청 없이 적용한다.
    if (draft.loadedUuid !== null && !dirty) {
      const existing = embodiments.find((entry) => entry.uuid === draft.loadedUuid);
      if (existing) {
        onApply(existing, draft.tip);
        return;
      }
    }
    setBusy(true);
    setError(null);
    try {
      let result: Embodiment;
      if (draft.loadedUuid === null) {
        result = await createEmbodiment(
          {
            end_effector_uuid: draft.endEffectorUuid as string,
            name: draft.name.trim(),
          },
          { role },
        );
      } else {
        result = await updateEmbodiment(
          draft.loadedUuid,
          {
            end_effector_uuid: draft.endEffectorUuid,
            name: draft.name.trim(),
          },
          { role },
        );
      }
      onApply(result, draft.tip);
    } catch (caught) {
      setError(resolveEmbodimentErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={requestClose}
      size="lg"
      style={{ maxWidth: "min(96vw, 60.8rem)" }}
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy && !closeConfirmOpen && catalogCreateDialog === null}
      aria-labelledby="embodiment-composer-title"
    >
      <HatModalHeader
        title="Embodiment를 구성합니다."
        titleId="embodiment-composer-title"
        close={{ disabled: busy }}
      />
      <div className={styles.body}>
        {error ? (
          <Text variant={hatcheryTextModes["caption-normal"]} className={styles.error} role="alert">
            {error}
          </Text>
        ) : null}

        <div className={styles.main}>
          <div className={styles.identityGroup}>
            <div className={styles.identityGrid}>
              <Field label="이름 *" layout="stack">
                <TextInput
                  value={draft.name}
                  disabled={busy}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    patchDraft({ name: event.target.value });
                  }}
                />
              </Field>
            </div>

            <div className={styles.hardwareGrid}>
              <Field label="End effector *" layout="stack">
                <CatalogSelect
                  label="End effector"
                  required
                  value={draft.endEffectorUuid}
                  options={endEffectors.map((ee) => ({
                    value: ee.uuid,
                    label: formatEndEffector(ee),
                  }))}
                  busy={busy}
                  onSelect={(value) => patchDraft({ endEffectorUuid: value })}
                  onRequestCreate={openCatalogCreateDialog}
                />
              </Field>
            </div>
          </div>
        </div>

        {closeConfirmOpen ? (
          <div className={styles.inlineDialogOverlay} role="presentation">
            <section className={styles.inlineDialog} role="dialog" aria-label="변경사항 닫기 확인">
              <Text variant={hatcheryTextModes["title-bold"]}>변경사항을 버릴까요?</Text>
              <Text
                variant={hatcheryTextModes["body-normal"]}
                className={styles.inlineDialogCaption}
              >
                저장하지 않은 Embodiment 구성 변경사항이 있습니다.
              </Text>
              <div className={styles.inlineDialogActions}>
                <IconButton
                  text="취소"
                  variant="surface"
                  onClick={() => setCloseConfirmOpen(false)}
                />
                <IconButton text="닫기" variant="solid" tone="danger" onClick={discardAndClose} />
              </div>
            </section>
          </div>
        ) : null}

        {catalogCreateDialog ? (
          <div className={styles.inlineDialogOverlay} role="presentation">
            <form
              className={styles.inlineDialog}
              role="dialog"
              aria-label="End effector 신규 추가"
              onSubmit={(event) => {
                event.preventDefault();
                void submitCatalogCreate();
              }}
            >
              <Text variant={hatcheryTextModes["title-bold"]}>End effector 신규 추가</Text>
              <TextInput
                value={catalogCreateDialog.draft}
                placeholder="이름을 입력해 주세요."
                aria-label="신규 항목 이름"
                disabled={catalogCreateDialog.submitting}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  updateCatalogCreateDraft(event.target.value)
                }
              />
              {catalogCreateDialog.errorMessage ? (
                <Text
                  variant={hatcheryTextModes["body-normal"]}
                  className={styles.error}
                  role="alert"
                >
                  {catalogCreateDialog.errorMessage}
                </Text>
              ) : null}
              <div className={styles.inlineDialogActions}>
                <IconButton
                  text="취소"
                  variant="surface"
                  disabled={catalogCreateDialog.submitting}
                  onClick={closeCatalogCreateDialog}
                />
                <IconButton
                  text={catalogCreateDialog.submitting ? "추가 중…" : "추가"}
                  variant="solid"
                  tone="primary"
                  disabled={
                    catalogCreateDialog.draft.trim() === "" || catalogCreateDialog.submitting
                  }
                  type="submit"
                />
              </div>
            </form>
          </div>
        ) : null}
      </div>
      <Modal.Footer>
        <span className={styles.summary}>
          {selectedEndEffector ? formatEndEffector(selectedEndEffector) : "EE 미선택"}
        </span>
        <IconButton
          text="취소"
          variant="surface"
          disabled={busy || closeConfirmOpen || catalogCreateDialog !== null}
          onClick={requestClose}
        />
        <IconButton
          text={busy ? "저장 중…" : "적용"}
          variant="solid"
          tone="primary"
          disabled={busy || loading || closeConfirmOpen || catalogCreateDialog !== null}
          onClick={() => void handleApply()}
        />
      </Modal.Footer>
    </Modal>
  );
}

// 카탈로그 선택이다. 일반 c6 드롭다운과, 검색 영역의 '신규' 버튼으로 공용 생성 모달을 연다.
// End effector 선택에 쓴다.
function CatalogSelect({
  label,
  value,
  options,
  busy,
  onSelect,
  required = false,
  onRequestCreate,
}: {
  label: string;
  value: string | null;
  options: readonly { value: string; label: string }[];
  busy: boolean;
  onSelect: (value: string | null) => void;
  required?: boolean;
  onRequestCreate?: () => void;
}): ReactNode {
  return (
    <div className={styles.catalog}>
      <div className={styles.catalogRow}>
        <SingleSelectDropdown
          mode={hatcheryDropdownModes.default}
          label={label}
          value={value}
          options={options}
          placeholder="항목을 선택해 주세요."
          required={required}
          searchable
          disabled={busy}
          className={styles.catalogDropdown}
          onValueChange={(next) => onSelect(next)}
          searchAction={
            onRequestCreate ? (
              <IconButton
                text="신규"
                variant="surface"
                size="sm"
                disabled={busy}
                onClick={onRequestCreate}
              />
            ) : undefined
          }
        />
      </div>
    </div>
  );
}
